import {
  getPricingTierBrandGroupLimit,
  getPricingTiers,
  type PricingTierLimit,
  type PricingTierPlanCode,
} from "@common/config/pricingCatalog";

export type BrandGroupAccessKind =
  | "loading"
  | "available"
  | "feature_not_included"
  | "downgraded_read_only"
  | "subscription_expired"
  | "limit_reached";

export interface BrandGroupAccessState {
  kind: BrandGroupAccessKind;
  currentCount: number;
  limit: PricingTierLimit;
  canCreate: boolean;
  isReadOnly: boolean;
  preferredTeamOfferCode: "PRO" | "TEAM_9999" | "TEAM_49999";
}

const LEGACY_TEAM_BRAND_GROUP_LIMIT = 20;

export const getBrandGroupLimitForPlanCode = (
  planCode?: string | null,
): PricingTierLimit => {
  const normalizedPlanCode = String(planCode || "")
    .trim()
    .toUpperCase();
  if (normalizedPlanCode === "TEAM") {
    return LEGACY_TEAM_BRAND_GROUP_LIMIT;
  }

  const tier = [...getPricingTiers("solo"), ...getPricingTiers("team")].find(
    (candidate) => candidate.planCode === normalizedPlanCode,
  );
  return tier ? getPricingTierBrandGroupLimit(tier) : 0;
};

export const getBrandGroupUpgradeTarget = (
  limit: PricingTierLimit,
): BrandGroupAccessState["preferredTeamOfferCode"] => {
  if (limit === 20) return "TEAM_9999";
  if (limit === 50) return "TEAM_49999";
  return "PRO";
};

/** A default Business group is included everywhere; only higher tiers can add more. */
export const canUseMultipleBusinessGroups = (
  limit: PricingTierLimit,
): boolean => limit === "unlimited" || limit > 1;

/**
 * Single-group navigation is a plan capability, not a snapshot of how many
 * groups happen to exist. Unknown plans stay out of this shortcut so a
 * multi-group directory is never hidden without an explicit limit.
 */
export const isSingleBusinessGroupPlan = ({
  planCode,
  featureMaxBrands,
}: {
  planCode?: string | null;
  featureMaxBrands?: number | null;
}): boolean => {
  const configuredLimit =
    featureMaxBrands && featureMaxBrands > 0
      ? featureMaxBrands
      : getBrandGroupLimitForPlanCode(planCode);
  return configuredLimit === 1;
};

export const resolveBrandGroupAccessState = ({
  planCode,
  subscriptionStatus,
  isExpired,
  isTrial,
  isLoading,
  featureMaxBrands,
  featureLoaded,
  currentCount,
}: {
  planCode?: string | null;
  subscriptionStatus?: string | null;
  isExpired?: boolean;
  isTrial?: boolean;
  isLoading?: boolean;
  featureLoaded?: boolean;
  featureEntitled?: boolean | null;
  featureMaxBrands?: number | null;
  currentCount: number;
}): BrandGroupAccessState => {
  const safeCurrentCount = Math.max(0, currentCount);
  const normalizedStatus = String(subscriptionStatus || "").toLowerCase();
  const configuredLimit =
    featureMaxBrands && featureMaxBrands > 0
      ? featureMaxBrands
      : getBrandGroupLimitForPlanCode(planCode);
  // Project management is available for every Workspace because every
  // Workspace is initialized with a default Project. Unknown/legacy plan
  // codes still get that default Project instead of a Team upsell gate.
  const limit =
    configuredLimit === "unlimited"
      ? configuredLimit
      : Math.max(1, configuredLimit);
  const preferredTeamOfferCode = getBrandGroupUpgradeTarget(limit);

  if (isLoading || featureLoaded === false) {
    return {
      kind: "loading",
      currentCount: safeCurrentCount,
      limit,
      canCreate: false,
      isReadOnly: true,
      preferredTeamOfferCode,
    };
  }

  if (
    isExpired ||
    normalizedStatus === "expired" ||
    normalizedStatus === "canceled"
  ) {
    return {
      kind: "subscription_expired",
      currentCount: safeCurrentCount,
      limit,
      canCreate: false,
      isReadOnly: true,
      preferredTeamOfferCode: limit === 0 ? "PRO" : preferredTeamOfferCode,
    };
  }

  if (limit !== "unlimited" && safeCurrentCount >= limit) {
    return {
      kind: "limit_reached",
      currentCount: safeCurrentCount,
      limit,
      canCreate: false,
      isReadOnly: false,
      preferredTeamOfferCode,
    };
  }

  return {
    kind: "available",
    currentCount: safeCurrentCount,
    limit,
    canCreate: true,
    isReadOnly: false,
    preferredTeamOfferCode,
  };
};

export type { PricingTierLimit, PricingTierPlanCode };
